import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, Headphones, BookOpen, Award, Wallet, LayoutDashboard, LogOut, User } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../store/useStore'

function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, accessTier, profilePicture } = useStore()

  // Store last visited page before auth
  useEffect(() => {
    if (!user && location.pathname !== '/auth' && location.pathname !== '/') {
      localStorage.setItem('lastVisitedPage', location.pathname)
    }
  }, [location, user])

  const navItems = [
    { label: 'Podcasts', path: '/podcasts', icon: Headphones },
    { label: 'Courses', path: '/courses', icon: BookOpen },
    { label: 'Pricing', path: '/pricing', icon: Wallet },
    { label: 'Ambassador Program', path: '/ambassador-program', icon: Award },
  ]

  // Add Dashboard to navItems if user exists
  const allNavItems = user 
    ? [...navItems, { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard }]
    : navItems

  const handleLogout = async () => {
    try {
      // Use relative URL or environment variable for API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      await fetch(`${apiBaseUrl}/api/auth`, {
        method: "POST",
        credentials: "include",
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Clear client-side state
      useStore.setState({
        user: null,
        accessTier: "free",
        enrolledCourses: [],
        completedLessons: {},
        userPoints: 0,
        userLevel: 1,
        badges: [],
        dailyStreak: 0,
      });

      // Clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('accessTier')
      localStorage.removeItem('adminToken')
      localStorage.removeItem('lastVisitedPage')
      
      // Close mobile menu if open
      setMenuOpen(false)
      
      // Navigate to home
      navigate('/')
      
      // Force reload to clear any cached auth state
      window.location.reload()
      
    } catch (error) {
      console.error("Logout failed:", error);
      
      // Even if server logout fails, clear client-side state
      useStore.setState({
        user: null,
        accessTier: "free",
        enrolledCourses: [],
        completedLessons: {},
        userPoints: 0,
        userLevel: 1,
        badges: [],
        dailyStreak: 0,
      });
      
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('accessTier')
      localStorage.removeItem('adminToken')
      localStorage.removeItem('lastVisitedPage')
      
      setMenuOpen(false)
      navigate('/')
    }
  }

  const handleAuthNavigation = () => {
    // Store current page before navigating to auth
    if (!user) {
      localStorage.setItem('lastVisitedPage', location.pathname)
    }
    navigate('/auth')
    setMenuOpen(false)
  }

  // Helper function to get initials
  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.trim().split(' ');
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    } else {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
  }

  // Simple link click handler - only for non-authenticated users trying to access dashboard
  const handleLinkClick = (path, isDashboard) => {
    if (isDashboard && !user) {
      // If trying to access dashboard without being logged in
      localStorage.setItem('lastVisitedPage', '/dashboard')
      navigate('/auth')
      setMenuOpen(false)
      return true // Return true to indicate navigation was handled
    }
    return false // Return false to let Link handle navigation
  }

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="../logo.png"
              alt="Saints Academy Logo"
              className="h-10 w-auto transition-transform duration-200 group-hover:scale-105"
            />
            <span className="text-xl font-bold text-gray-900">Saints Academy</span>
          </Link>

          {/* Desktop Navigation - SIMPLIFIED */}
          <nav className="hidden md:flex items-center space-x-8">
            {allNavItems.map((item) => {
              const Icon = item.icon
              const isDashboard = item.path === '/dashboard'
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => {
                    // Only prevent default if user is not logged in and trying to access dashboard
                    if (isDashboard && !user) {
                      e.preventDefault()
                      localStorage.setItem('lastVisitedPage', '/dashboard')
                      navigate('/auth')
                    }
                  }}
                  className={`flex items-center space-x-2 text-gray-700 hover:text-primary transition-colors duration-200 font-medium ${
                    location.pathname === item.path ? 'text-primary font-semibold' : ''
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-full capitalize">
                  {accessTier}
                </span>
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold overflow-hidden">
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : user?.name ? (
                    <span>{getInitials(user.name)}</span>
                  ) : (
                    <User className="w-6 h-6" />
                  )}
                </div>
                <span className="text-gray-700">Hi, {user.name || user.username}</span>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleAuthNavigation}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-md"
                >
                  Start Quest
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - SIMPLIFIED */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-4 space-y-3">
              {allNavItems.map((item) => {
                const Icon = item.icon
                const isDashboard = item.path === '/dashboard'
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      if (isDashboard && !user) {
                        // If trying to access dashboard without login, redirect to auth
                        localStorage.setItem('lastVisitedPage', '/dashboard')
                        navigate('/auth')
                      } else {
                        // Otherwise just close the menu
                        setMenuOpen(false)
                      }
                    }}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 ${
                      location.pathname === item.path ? 'bg-gray-50' : ''
                    }`}
                  >
                    <Icon className="w-5 h-5 text-primary" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                )
              })}
              
              {user ? (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold overflow-hidden">
                        {profilePicture ? (
                          <img 
                            src={profilePicture} 
                            alt="Profile" 
                            className="w-full h-full object-cover"
                          />
                        ) : user?.name ? (
                          <span>{getInitials(user.name)}</span>
                        ) : (
                          <User className="w-6 h-6" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.name || user.username}</div>
                        <div className="text-sm text-gray-600 capitalize">{accessTier} tier</div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors duration-200"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                  <button
                    onClick={handleAuthNavigation}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-md"
                  >
                    Start Quest
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header